import { Divider, Button, List, message, Modal, Spin, Tooltip, Input } from "antd";
import { EyeOutlined, DeleteOutlined, DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import React, { useEffect, useState, useRef } from "react";
import { getAttachment, deleteAttachment, postAttachment, downloadAttachment, downloadAttachmentPreview } from "../../api/postAPI";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer"

interface CustomFile {
  originalFileName: string;
  savedFileName: string;
}

interface AttachmentProps {
  postId: number; // Post ID를 props로 전달받음
  isDeleteButtonVisible?: boolean; // 삭제 버튼 표시 여부
  isUploadVisible?: boolean; // 업로드 영역 표시 여부 (협력 사이트 게스트 등 읽기 전용이면 false)
}

const Attatchment: React.FC<AttachmentProps> = ({ postId, isDeleteButtonVisible=false, isUploadVisible=true }) => {
  const [fileList, setFileList] = useState<CustomFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [previewFileType, setPreviewFileType] = useState<string>("");
  const [previewFileName, setPreviewFileName] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 업로드 전 파일명 지정 단계
  const [pendingItems, setPendingItems] = useState<{ file: File; base: string; ext: string }[]>([]);
  const [renameModalOpen, setRenameModalOpen] = useState(false);


  // 미리보기 지원 가능한 확장자 목록 ( https://www.npmjs.com/package/@cyntler/react-doc-viewer )
  const supportedPreviewExtensions = [
    "bmp",
    "gif",
    "jpg",
    "jpeg",
    "pdf",
    "png",
    "tiff",
    "png",
    "txt",
    "mp4",
    "webp",
    "ppt",
    "pptx"
  ];

  // 파일 목록 가져오기
  const handleGetList = async () => {
    try {
      const data = await getAttachment(postId);
      setFileList(data);
    } catch (error) {
      console.error("Error fetching attachments:", error);
      // message.error("Failed to fetch attachment list.");
    }
  };

  // 파일명을 확장자와 분리
  const splitFileName = (name: string) => {
    const dot = name.lastIndexOf(".");
    if (dot > 0) return { base: name.slice(0, dot), ext: name.slice(dot) };
    return { base: name, ext: "" };
  };

  // 실제 업로드 (지정한 파일명으로)
  const uploadFiles = async (items: { file: File; name: string }[]) => {
    if (items.length === 0) return;

    setIsUploading(true);
    let permissionDenied = false;
    const promises = items.map(async ({ file, name }) => {
      const formData = new FormData();
      // 세 번째 인자로 서버에 전달될 파일명을 지정 (원본 파일명 대체)
      formData.append("file", file, name);
      try {
        await postAttachment(postId, formData);
        message.success(`"${name}" 파일이 업로드되었습니다.`);
      } catch (error) {
        if (error?.response?.status === 403) {
          permissionDenied = true;
          return;
        }
        const serverMessage = error?.response?.data?.message;
        if (serverMessage) {
          message.error(serverMessage);
        }
        return;
      }
    });

    try {
      await Promise.all(promises);
      if (permissionDenied) {
        message.error("첨부파일을 추가할 권한이 없습니다.");
      }
      handleGetList();
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // 파일 선택/드롭 시: 곧바로 올리지 않고 파일명 지정 모달을 연다.
  const openRenameModal = (files: FileList | File[] | null) => {
    if (!files) return;
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setPendingItems(
      arr.map((file) => {
        const { base, ext } = splitFileName(file.name);
        return { file, base, ext };
      })
    );
    setRenameModalOpen(true);
  };

  const handleRenameChange = (index: number, value: string) => {
    setPendingItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, base: value } : it))
    );
  };

  const closeRenameModal = () => {
    if (isUploading) return;
    setRenameModalOpen(false);
    setPendingItems([]);
  };

  // 파일명 지정 후 업로드 실행
  const handleConfirmUpload = async () => {
    const items = pendingItems.map(({ file, base, ext }) => {
      const trimmed = base.trim();
      // 이름을 비워두면 원본 파일명 유지
      const finalBase = trimmed ? trimmed : splitFileName(file.name).base;
      return { file, name: `${finalBase}${ext}` };
    });
    await uploadFiles(items);
    setRenameModalOpen(false);
    setPendingItems([]);
  };

  // input(파일 선택) 핸들러
  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    openRenameModal(event.target.files);
    event.target.value = ""; // 동일 파일 재선택 가능하도록 초기화
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.types?.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (isUploading) return;
    openRenameModal(e.dataTransfer?.files ?? null);
  };

  // 파일 삭제 핸들러
  const handleDelete = (savedFileName: string, originalFileName: string) => {
    const { confirm } = Modal;

    confirm({
      title: `파일을 삭제하시겠습니까?`,
      content: `파일명: ${originalFileName}`,
      okText: "삭제",
      okType: "danger",
      cancelText: "취소",
      centered: true,
      onOk: async () => {
        try {
          await deleteAttachment(postId, savedFileName);
          message.success("File deleted successfully.");
          handleGetList(); // 삭제 후 목록 갱신
        } catch (error) {
          if (error?.response?.status === 403) {
            message.error("첨부파일을 삭제할 권한이 없습니다.");
            return;
          }
          const serverMessage = error?.response?.data?.message;
          if (serverMessage) {
            message.error(serverMessage);
          }
          // console.error("Error deleting file:", error);
          // message.error("Failed to delete file.");
        }
      },
      onCancel() {
        console.log("취소됨");
      },
    });
  };

  // 파일 다운로드 핸들러
  // const handleDownload = async (savedFileName: string, originalFileName: string) => {
  //   try {
  //     const blob = await downloadAttachment(savedFileName); // Blob 데이터 가져오기

  //     const url = window.URL.createObjectURL(new Blob([blob]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", originalFileName);
  //     document.body.appendChild(link);
  //     link.click();
  //     window.URL.revokeObjectURL(url);
  //     link.parentNode?.removeChild(link);

  //     message.success(`File "${originalFileName}" downloaded successfully.`);
  //   } catch (error) {
  //     console.error("Error downloading file:", error);
  //     message.error("Failed to download file.");
  //   }
  // };

  // 미리보기 지원 여부 확인
  const isPreviewSupported = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    return supportedPreviewExtensions.includes(ext);
  };

  // 파일 미리보기 핸들러
  const handlePreview = async (savedFileName: string, originalFileName: string) => {
    if (!isPreviewSupported(savedFileName)) {
      message.error("미리보기를 지원하지 않는 파일 형식입니다.");
      return;
    }

    // 모달을 즉시 열고 로딩 상태를 보여준 뒤 파일을 불러온다.
    setPreviewFileName(originalFileName);
    setPreviewVisible(true);
    setPreviewLoading(true);

    try {
      const blob = await downloadAttachmentPreview(savedFileName);
      const url = window.URL.createObjectURL(new Blob([blob]));
      setPreviewURL(url);
      setPreviewFileType(convertBlobFileTypeToExtension(blob));
    } catch (error) {
      console.error("Error previewing file:", error);
      message.error("파일 미리보기에 실패했습니다.");
      setPreviewVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // blob 확장자 변환
  const convertBlobFileTypeToExtension = (blob: Blob) => {
    const fileType = blob.type;
    let extension = "";

    switch (fileType) {
      case "application/pdf":
        extension = "pdf";
        break;
      case "image/jpeg":
      case "image/jpg":
        extension = "jpg";
        break;
      case "image/png":
        extension = "png";
        break;
      case "image/bmp":
        extension = "bmp";
        break;
      case "image/gif":
        extension = "gif";
        break;
      case "image/tiff":
        extension = "tiff";
        break;
      case "text/plain":
        extension = "txt";
        break;
      case "video/mp4":
        extension = "mp4";
        break;
      case "image/webp":
        extension = "webp";
        break;
      default:
        extension = "";
    }
    
    

    return extension;
  }
  

  useEffect(() => {
    handleGetList();
  }, [postId]);

  return (
    <div
      style={{
        width: "100%",
        marginBottom: "3rem",
      }}
    >
      <Divider style={{ fontSize: "1.4rem" }}>Attachment</Divider>

      {/* 파일 업로드 */}
      <div
        style={{
          width: "80%",
          maxWidth: "84rem",
          margin: "0 auto",
        }}
      >
        {isUploadVisible && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              id="file-upload"
              style={{ display: "none" }}
            />
            <div
              onClick={() => !isUploading && fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "1rem",
                padding: "1.75rem 1rem",
                border: `2px dashed ${isDragging ? "#1890ff" : "#d9d9d9"}`,
                borderRadius: "8px",
                background: isDragging ? "#e6f4ff" : "#fafafa",
                color: isDragging ? "#1890ff" : "#888",
                cursor: isUploading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                textAlign: "center",
                userSelect: "none",
              }}
            >
              {isUploading ? (
                <>
                  <Spin />
                  <div style={{ fontSize: "0.9rem" }}>업로드 중...</div>
                </>
              ) : (
                <>
                  <InboxOutlined style={{ fontSize: "2rem", color: isDragging ? "#1890ff" : "#bbb" }} />
                  <div style={{ fontSize: "0.95rem", fontWeight: 500 }}>
                    파일을 이곳에 드래그하거나 클릭하여 선택하세요
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "#aaa" }}>
                    여러 파일을 한 번에 첨부할 수 있습니다.
                  </div>
                </>
              )}
            </div>
          </>
        )}
        {/* 파일 목록 */}
        <List
          header={<div style={{ fontWeight: "bold" }}>Uploaded Files</div>}
          bordered
          dataSource={fileList}
          renderItem={(file) => (
            <List.Item
              actions={[
                <Tooltip
                  title={
                    isPreviewSupported(file.savedFileName)
                      ? "미리보기"
                      : "미리보기를 지원하지 않는 형식입니다"
                  }
                >
                  <span style={{ cursor: isPreviewSupported(file.savedFileName) ? "pointer" : "not-allowed" }}>
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      disabled={!isPreviewSupported(file.savedFileName)}
                      style={!isPreviewSupported(file.savedFileName) ? { pointerEvents: "none" } : undefined}
                      onClick={() => handlePreview(file.savedFileName, file.originalFileName)}
                    />
                  </span>
                </Tooltip>,
                // <Button
                //   type="text"
                //   icon={<DownloadOutlined />}
                //   onClick={() => handleDownload(file.savedFileName, file.originalFileName)}
                // >
                // </Button>,
                isDeleteButtonVisible && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(file.savedFileName, file.originalFileName)}
                  />
                ),
              ]}
            >
              {file.originalFileName}
            </List.Item>
          )}
          style={{
            background: "#fff",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* 미리보기 모달 */}
      <Modal
        open={previewVisible}
        title={previewFileName || "파일 미리보기"}
        centered
        footer={null}
        // 사이트 고정 헤더(z-index 9999~10001)보다 위에 표시되어야
        // PDF 페이지 넘김 등 상단 컨트롤이 헤더에 가려지지 않는다.
        zIndex={10050}
        onCancel={() => {
          setPreviewVisible(false);
          if (previewURL) {
            window.URL.revokeObjectURL(previewURL);
            setPreviewURL(null);
          }
          if (previewFileType) {
            setPreviewFileType("");
          }
          if(previewFileName) {
            setPreviewFileName("");
          }
          setPreviewLoading(false);
        }}
        width="100%"
        style={{ maxWidth: "100rem", margin: "0 auto" }}
        styles={{ body: { padding: 0, maxHeight: "85vh", overflow: "auto" } }}
      >
        {previewLoading ? (
          <div
            style={{
              height: "60vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
              color: "#888",
            }}
          >
            <Spin size="large" />
            <div>미리보기를 불러오는 중...</div>
          </div>
        ) : previewURL ? (
          // 스크롤은 모달 body(maxHeight/overflow)에서 처리한다. DocViewer에는 고정 높이를
          // 주지 않아 내용만큼 렌더되고, 내용이 길면 모달 body가 스크롤된다. 페이지 넘김
          // 컨트롤(pdf-controls)은 sticky라 스크롤해도 상단에 계속 보인다.
          <div onContextMenu={e => e.preventDefault()}>
            {/* react-doc-viewer 내부 컨테이너의 자체 스크롤을 풀어, 스크롤은 모달 body가
                담당하게 한다. 그러면 하단까지 스크롤되면서도 PDF 페이지 넘김 컨트롤
                (#pdf-controls)은 sticky로 상단에 계속 표시된다. */}
            <style>{`
              #proxy-renderer { overflow: visible !important; }
              /* 스크롤을 내려도 페이지가 컨트롤 위로 겹쳐 클릭을 가로채지 않도록,
                 컨트롤을 불투명 배경 + 높은 z-index 로 항상 최상단에 고정한다. */
              #pdf-controls {
                position: sticky !important;
                top: 0;
                z-index: 1000 !important;
                background: #fff !important;
              }
              #pdf-controls button { pointer-events: auto !important; }
            `}</style>
            <DocViewer
              key={previewURL}
              documents={[{ uri: previewURL, fileType: previewFileType, fileName: previewFileName }]}
              pluginRenderers={DocViewerRenderers}
              config={{ header: { disableHeader: true, disableFileName: true } }}
            />
          </div>
        ) : null}
      </Modal>

      {/* 파일명 지정 모달 */}
      <Modal
        open={renameModalOpen}
        title="첨부파일 이름 지정"
        okText="업로드"
        cancelText="취소"
        confirmLoading={isUploading}
        onOk={handleConfirmUpload}
        onCancel={closeRenameModal}
        maskClosable={!isUploading}
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
          <div style={{ fontSize: 13, color: "#888" }}>
            필요 시 파일명을 변경한 뒤 업로드하세요. 확장자는 그대로 유지됩니다.
          </div>
          {pendingItems.map((item, idx) => (
            <Input
              key={idx}
              value={item.base}
              addonAfter={item.ext || undefined}
              onChange={(e) => handleRenameChange(idx, e.target.value)}
              placeholder="파일명"
              disabled={isUploading}
            />
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Attatchment;
